import React, { useState, useEffect } from 'react'
import { Flex } from 'reflexbox/styled-components'
import { Formik, Form } from 'formik'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'

import moment from 'moment'

import Button from 'components/Button'
import Card from 'components/Card'
import Breadcrumb from 'components/Breadcrumb'
import Loader from 'components/Loader'
import { RadioGroup, TextField, NumberField, DateRangePickerField, CheckboxField, Dropzone } from 'components/Form'
import { Title, SemiTitle, Text } from 'components/Typography'

import { registerNewDiscountCoupon } from 'services/coupons'
import { fetchNewReferenceCode } from 'services/commissioneds'

import CouponSchema from 'schemas/staff/coupon'

const BreadcrumbPath = [
  {
    label: 'Cupons',
    link: '/staff/promotions/coupons',
  },
  {
    label: 'Cadastrar cupom',
  },
]

const New = () => {
  const [generatedReferenceCode, setGeneratedReferenceCode] = useState(null)
  const [csvError, setCsvError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const history = useHistory()

  useEffect(() => {
    const fetchNewCode = async () => {
      try {
        setIsLoading(true)
        const code = await fetchNewReferenceCode()
        setGeneratedReferenceCode(code)
      } catch (e) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }

    fetchNewCode()
  }, [])

  const onSubmit = async ({ status, ...values }) => {
    if (values.permission && !values.clients_list) {
      toast.error('É necessário fazer o upload de um arquivo CSV')
      return
    }

    try {
      const formattedData = new FormData()
      formattedData.append('value', values.value)
      formattedData.append('code', values.code)
      formattedData.append('description', values.description)
      formattedData.append('initial_date', moment(values.expiration_date.startDate).format('YYYY-MM-DD'))
      formattedData.append('expiration_date', moment(values.expiration_date.endDate).format('YYYY-MM-DD'))
      formattedData.append('specific_users_only', values.permission)
      formattedData.append('active', status === 'active')
      if (values.permission) formattedData.append('file', values.clients_list)

      const { data } = await registerNewDiscountCoupon(formattedData)

      toast.success('Cupom cadastrado com sucesso')

      history.push(`/staff/promotions/coupons/${data.id}`)
    } catch (e) {
      console.log(e)
      if (e?.response?.data?.errors) {
        const formattedError = e?.response?.data?.errors?.map(error => (
          <Flex>
            <SemiTitle color='error'>Linha {error.line}:&nbsp;</SemiTitle>
            <Text color='error'>{error.error.map(log => log).join(' ')}</Text>
          </Flex>
        ))
        setCsvError(formattedError)
      } else {
        toast.error('Não foi possível realizar o cadastro do cupom')
      }
    }
  }

  if (isLoading)
    return (
      <Flex minHeight='80vh' justifyContent='center'>
        <Loader />
      </Flex>
    )

  return (
    <Flex flexDirection='column'>
      <Breadcrumb children={BreadcrumbPath} />

      <Title as='h3'>Cadastrar Cupom</Title>

      <Formik
        initialValues={{
          status: 'active',
          description: '',
          code: generatedReferenceCode,
          value: undefined,
          expiration_date: { startDate: undefined, endDate: undefined },
          permission: false,
        }}
        validationSchema={CouponSchema}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <Card mt='24px'>
              <Flex flexDirection='column' width={[1, '384px']}>
                <RadioGroup
                  mb='32px'
                  label='Status'
                  name='status'
                  direction='row'
                  options={[
                    { label: 'Ativo', value: 'active' },
                    { label: 'Inativo', value: 'inactive' },
                  ]}
                />

                <Flex flexDirection='column'>
                  <SemiTitle color='grays.N500'>Permissão</SemiTitle>
                  <CheckboxField name='permission' label='Limitar clientes' mt={2} mb='32px' />
                </Flex>
                {values.permission && (
                  <Dropzone
                    setFieldValue={setFieldValue}
                    fileLink='https://guigotv-staging.s3-us-west-2.amazonaws.com/exemplo.csv'
                    name='clients_list'
                    label='Upload da lista de clientes'
                    errors={csvError}
                    mb='32px'
                  />
                )}

                <TextField mb='32px' name='description' label='Descrição' placeholder='Digite aqui a descrição' />
                <TextField mb='32px' name='code' label='Código de Cupom' placeholder='Digite aqui o código do cupom' />

                <NumberField
                  mb='32px'
                  name='value'
                  label='Valor do cupom'
                  placeholder='Insira aqui o valor do cupom'
                  prefix='R$ '
                />

                <DateRangePickerField
                  mb='32px'
                  name='expiration_date'
                  label='Validade'
                  isOutsideRange={date => date.isBefore(moment(), 'day')}
                />
              </Flex>
              <Flex width={[1, '384px']} justifyContent='space-between'>
                <Button type='button' outlined onClick={() => history.push('/staff/promotions/coupons')}>
                  Voltar
                </Button>
                <Button type='submit' disabled={isSubmitting}>
                  {isSubmitting ? <Loader /> : 'Cadastrar'}
                </Button>
              </Flex>
            </Card>
          </Form>
        )}
      </Formik>
    </Flex>
  )
}

export default New
